import React from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useDrawerDirty } from "@shell/components/DrawerShell";
import { SelectCard } from "@shell/components/SelectCard";

import type {
  CreateQueueRequest,
  NatsTransportSpec,
  ProvisionModeUI,
  UpdateQueueRequest,
} from "../../../types";

import { useQueueForm } from "./useQueueForm";

type QueueType = "rabbitmq" | "nats" | "kafka" | "pulsar";

const QUEUE_TYPES: { value: QueueType; label: string; description: string }[] =
  [
    {
      value: "rabbitmq",
      label: "RabbitMQ",
      description: "AMQP broker, flexible routing",
    },
    {
      value: "nats",
      label: "NATS",
      description: "Lightweight pub/sub, JetStream",
    },
    {
      value: "kafka",
      label: "Kafka",
      description: "Partitioned log, high throughput",
    },
    {
      value: "pulsar",
      label: "Pulsar",
      description: "Multi-tenant, geo-replication",
    },
  ];

export interface QueueFormHandle {
  submit: () => boolean;
  getState: () => ReturnType<typeof useQueueForm>["state"];
}

export interface QueueFormProps {
  onSubmit: (req: CreateQueueRequest) => void;
  isSubmitting: boolean;
  /** Edit mode: existing streams shown read-only, only new streams editable. */
  isEdit?: boolean;
  /** Existing stream specs to display as read-only in edit mode. */
  existingStreams?: NatsTransportSpec[];
  /** Resource name (read-only in edit mode). */
  resourceName?: string;
  /** Called with the update request in edit mode. */
  onUpdateSubmit?: (req: UpdateQueueRequest) => void;
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      component="button"
      onClick={onChange}
      disabled={disabled}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        p: 1.25,
        border: "none",
        borderRadius: 1,
        bgcolor: "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "left",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      {checked ? (
        <ToggleOnIcon color="primary" />
      ) : (
        <ToggleOffIcon color="disabled" />
      )}
    </Box>
  );
}

/** Render a single NATS JetStream entry inside the multi-stream accordion. */
function StreamEntry({
  index,
  stream,
  error,
  isSubmitting,
  onUpdate,
  onRemove,
  canRemove,
  readOnly = false,
}: {
  index: number;
  stream: ReturnType<typeof useQueueForm>["state"]["streams"][number];
  error?: string;
  isSubmitting: boolean;
  onUpdate: <K extends keyof typeof stream>(
    field: K,
    value: (typeof stream)[K],
  ) => void;
  onRemove: () => void;
  canRemove: boolean;
  readOnly?: boolean;
}) {
  const label = readOnly
    ? `${stream.streamName.trim() || `Stream ${index + 1}`} — Existing`
    : stream.streamName.trim() || `Stream ${index + 1}`;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <IconButton
          size="small"
          onClick={onRemove}
          disabled={isSubmitting || !canRemove || readOnly}
          aria-label={`Remove ${label}`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack spacing={1.5}>
        <TextField
          label="JetStream stream name"
          size="small"
          placeholder="e.g. PROJECT_REPORTS"
          value={stream.streamName}
          onChange={(e) => onUpdate("streamName", e.target.value)}
          helperText="Uppercase stream name for this application transport"
          disabled={isSubmitting || readOnly}
          error={error?.includes("Stream name") || error?.includes("Duplicate")}
        />
        <TextField
          label="Subject prefix"
          size="small"
          placeholder="e.g. reports"
          value={stream.subjectPrefix}
          onChange={(e) => onUpdate("subjectPrefix", e.target.value)}
          helperText="Relative tokens only; Rusha adds the organization and project scope"
          disabled={isSubmitting || readOnly}
          error={error?.includes("Subject prefix")}
        />
        <Divider sx={{ my: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Default subjects (editable)
          </Typography>
        </Divider>
        <TextField
          label="Command subjects"
          size="small"
          multiline
          value={stream.commands}
          onChange={(e) => onUpdate("commands", e.target.value)}
          helperText="Comma-separated relative command subjects"
          disabled={isSubmitting || readOnly}
          error={error?.includes("command subject")}
        />
        <TextField
          label="Lifecycle subjects"
          size="small"
          multiline
          value={stream.lifecycleEvents}
          onChange={(e) => onUpdate("lifecycleEvents", e.target.value)}
          helperText="Comma-separated relative lifecycle subjects consumed by the API"
          disabled={isSubmitting || readOnly}
          error={error?.includes("lifecycle subject")}
        />
        <TextField
          label="Dead-letter subject"
          size="small"
          value={stream.deadLetter}
          onChange={(e) => onUpdate("deadLetter", e.target.value)}
          disabled={isSubmitting || readOnly}
          error={error?.includes("Dead-letter")}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            fullWidth
            label="API durable consumer"
            size="small"
            value={stream.apiConsumer}
            onChange={(e) => onUpdate("apiConsumer", e.target.value)}
            disabled={isSubmitting || readOnly}
            error={error?.includes("API consumer")}
          />
          <TextField
            fullWidth
            label="Worker durable consumer"
            size="small"
            value={stream.workerConsumer}
            onChange={(e) => onUpdate("workerConsumer", e.target.value)}
            disabled={isSubmitting || readOnly}
            error={error?.includes("Worker consumer")}
          />
        </Stack>
        {error && (
          <Alert severity="error" role="alert" sx={{ py: 0.5 }}>
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}

export const QueueForm = React.forwardRef<QueueFormHandle, QueueFormProps>(
  function QueueForm(
    {
      onSubmit,
      isSubmitting,
      isEdit = false,
      existingStreams = [],
      resourceName = "",
      onUpdateSubmit,
    },
    ref,
  ): React.ReactElement {
    const { setIsDirty } = useDrawerDirty();
    const form = useQueueForm({ isEdit, existingStreams, resourceName });
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    const handleSubmit = () => {
      if (!form.validate()) {
        nameInputRef.current?.focus();
        return false;
      }
      if (isEdit && onUpdateSubmit) {
        onUpdateSubmit(form.buildUpdateRequest());
      } else {
        onSubmit(form.buildRequest());
      }
      return true;
    };

    React.useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      getState: () => form.state,
    }));

    return (
      <Box
        component="form"
        onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <Stack spacing={2.5}>
          {/* Name */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Queue Name
            </Typography>
            <TextField
              id="queue-name"
              inputRef={nameInputRef}
              fullWidth
              size="small"
              placeholder="my_queue"
              value={form.state.name}
              onChange={(e) => {
                form.setField("name", e.target.value);
                setIsDirty(true);
              }}
              onBlur={() => form.onBlur("name")}
              error={!!form.errors.name}
              helperText={
                form.errors.name ??
                "Start with a letter or underscore; use only letters, numbers, and underscores. Example: events_queue."
              }
              FormHelperTextProps={{ role: "alert" }}
              disabled={isSubmitting || isEdit}
            />
          </Box>

          {/* Queue Type and Mode — hidden in edit mode */}
          {!isEdit && (
            <>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Queue Type
                </Typography>
                <Stack spacing={1}>
                  {QUEUE_TYPES.map((qt) => (
                    <SelectCard
                      key={qt.value}
                      label={qt.label}
                      description={qt.description}
                      selected={form.state.queueType === qt.value}
                      onClick={() => {
                        form.setField("queueType", qt.value);
                        if (
                          qt.value === "nats" &&
                          form.state.mode === "dedicated"
                        ) {
                          form.setField("mode", "shared");
                        }
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Mode */}
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Mode
                </Typography>
                <Stack spacing={1}>
                  <SelectCard
                    label="Shared"
                    description={`Isolated namespace on shared ${form.state.queueType} cluster`}
                    selected={form.state.mode === "shared"}
                    onClick={() => {
                      form.setField("mode", "shared" as ProvisionModeUI);
                      setIsDirty(true);
                    }}
                    disabled={isSubmitting}
                  />
                  <SelectCard
                    label="Dedicated"
                    description="Own cluster with dedicated resources"
                    selected={form.state.mode === "dedicated"}
                    onClick={() => {
                      form.setField("mode", "dedicated" as ProvisionModeUI);
                      setIsDirty(true);
                    }}
                    disabled={isSubmitting || form.state.queueType === "nats"}
                  />
                </Stack>
              </Box>
            </>
          )}

          {form.state.queueType === "nats" && (
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    NATS application transport
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.state.streams.length === 1
                      ? "Creates the stream, durable consumers, and subject permissions during provisioning."
                      : `${form.state.streams.length} streams — each creates its own JetStream stream, consumers, and subject permissions.`}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {form.state.streams.map((stream, i) => (
                    <StreamEntry
                      key={i}
                      index={i}
                      stream={stream}
                      error={form.errors.streamErrors?.[i]}
                      isSubmitting={isSubmitting}
                      onUpdate={(field, value) => {
                        form.updateStream(i, field, value);
                        setIsDirty(true);
                      }}
                      onRemove={() => {
                        form.removeStream(i);
                        setIsDirty(true);
                      }}
                      canRemove={
                        isEdit
                          ? form.state.streams.length - form.readOnlyCount > 1
                          : form.state.streams.length > 1
                      }
                      readOnly={isEdit && i < form.readOnlyCount}
                    />
                  ))}

                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      form.addStream();
                      setIsDirty(true);
                    }}
                    disabled={isSubmitting}
                    sx={{
                      alignSelf: "flex-start",
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Add Stream
                  </Button>

                  <Alert severity="info">
                    The generated credential is scoped to this project
                    transport. The NATS subject root is enforced server-side.
                  </Alert>

                  {form.errors.natsTransport && !form.errors.streamErrors && (
                    <Alert severity="error" role="alert">
                      {form.errors.natsTransport}
                    </Alert>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Type-specific dedicated fields */}
          {form.state.mode === "dedicated" && (
            <Stack spacing={2.5} sx={{ animation: "fadeIn 200ms ease" }}>
              {form.state.queueType === "rabbitmq" && (
                <>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Replicas
                    </Typography>
                    <Stack spacing={1}>
                      <SelectCard
                        label="1 node"
                        description="Single broker (no HA)"
                        selected={form.state.replicas === 1}
                        onClick={() => {
                          form.setField("replicas", 1);
                          setIsDirty(true);
                        }}
                        disabled={isSubmitting}
                      />
                      <SelectCard
                        label="3 nodes"
                        description="Quorum queue HA"
                        selected={form.state.replicas === 3}
                        onClick={() => {
                          form.setField("replicas", 3);
                          setIsDirty(true);
                        }}
                        disabled={isSubmitting}
                      />
                    </Stack>
                  </Box>
                  <ToggleRow
                    label="Persistence"
                    description="Durable messages on disk"
                    checked={form.state.persistence}
                    onChange={() => {
                      form.setField("persistence", !form.state.persistence);
                      setIsDirty(true);
                    }}
                    disabled={isSubmitting}
                  />
                  <ToggleRow
                    label="Management UI"
                    description="RabbitMQ management plugin"
                    checked={form.state.managementUi}
                    onChange={() => {
                      form.setField("managementUi", !form.state.managementUi);
                      setIsDirty(true);
                    }}
                    disabled={isSubmitting}
                  />
                </>
              )}

              {form.state.queueType === "nats" && (
                <ToggleRow
                  label="JetStream"
                  description="Persistent streaming and key-value"
                  checked={form.state.jetStream}
                  onChange={() => {
                    form.setField("jetStream", !form.state.jetStream);
                    setIsDirty(true);
                  }}
                  disabled={isSubmitting}
                />
              )}

              {form.state.queueType === "kafka" && (
                <>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Partitions
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={form.state.partitions}
                      onChange={(e) => {
                        form.setField("partitions", Number(e.target.value));
                        setIsDirty(true);
                      }}
                      disabled={isSubmitting}
                      inputProps={{ min: 1, max: 100 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Replication Factor
                    </Typography>
                    <Stack spacing={1}>
                      <SelectCard
                        label="1"
                        description="No replication"
                        selected={form.state.replicationFactor === 1}
                        onClick={() => {
                          form.setField("replicationFactor", 1);
                          setIsDirty(true);
                        }}
                        disabled={isSubmitting}
                      />
                      <SelectCard
                        label="3"
                        description="3 replicas for HA"
                        selected={form.state.replicationFactor === 3}
                        onClick={() => {
                          form.setField("replicationFactor", 3);
                          setIsDirty(true);
                        }}
                        disabled={isSubmitting}
                      />
                    </Stack>
                  </Box>
                </>
              )}

              {form.state.queueType === "pulsar" && (
                <Alert
                  severity="info"
                  sx={{ "& .MuiAlert-message": { fontSize: "0.875rem" } }}
                >
                  Pulsar dedicated clusters are provisioned with default broker
                  and bookie counts. Advanced configuration is available via
                  API.
                </Alert>
              )}
            </Stack>
          )}

          {/* Advanced */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                Advanced
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <ToggleRow
                  label="TLS"
                  description="Encrypt connections"
                  checked={form.state.tls}
                  onChange={() => {
                    form.setField("tls", !form.state.tls);
                    setIsDirty(true);
                  }}
                  disabled={isSubmitting}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block" }}
                  >
                    Authentication
                  </Typography>
                  <Stack spacing={0.5}>
                    {(["none", "token", "mtls"] as const).map((a) => (
                      <SelectCard
                        key={a}
                        label={
                          a === "mtls"
                            ? "mTLS"
                            : a.charAt(0).toUpperCase() + a.slice(1)
                        }
                        selected={form.state.auth === a}
                        onClick={() => {
                          form.setField("auth", a);
                          setIsDirty(true);
                        }}
                        disabled={isSubmitting}
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        </Stack>
      </Box>
    );
  },
);

QueueForm.displayName = "QueueForm";
